import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link, useNavigate } from 'react-router-dom';
import Input from '../../components/form/Input';
import { employeeSchema } from '../../Helpers/Yup';
import { useDispatch, useSelector } from 'react-redux';
import { clearError, loginEmployee } from '../../redux/slices/employeeSlice';
import '../website/website.css';

export default function EmployeeLogin() {
   const dispatch = useDispatch();
    const navigate = useNavigate();

    const { employeeLoading, error, isAuthenticated, passwordUpdateRequired } = useSelector((state) => state.employee);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(employeeSchema),
  });

    useEffect(() => {
      if (isAuthenticated) {
        navigate('/employee/dashboard');
      } else if (passwordUpdateRequired) {
        // Redirect to password update screen if required
        navigate('/employee/update-password');
      }
      return () => {
        dispatch(clearError());
      };
    }, [isAuthenticated, passwordUpdateRequired, navigate, dispatch]);

  const onSubmit = async (data) => {
    dispatch(loginEmployee(data));
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image/Branding */}
      <div className="hidden lg:flex lg:w-1/2 main-bg items-center justify-center p-12">
        <div className="max-w-md text-center">
          <div className="mb-8">
            <div className="text-4xl font-bold text-white mb-4">
              xy<span className="bg-gradient-to-r from-yellow-300 to-white bg-clip-text text-transparent">leads</span>
            </div>
            <p className="text-xl text-white text-opacity-90 leading-relaxed">
              Your reliable partner in call management
            </p>
          </div>
          
          {/* Sample illustration */}
          <div className="glass-card-white p-8 rounded-2xl">
            <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
              </svg>
            </div>
            <h3 className="text-lg font-semibold gradient-text mb-2">Employee Dashboard</h3>
            <p className="text-gray-600 text-sm">
              Access your call analytics, view reports, and track performance metrics.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-4 text-center">
            <div className="text-white text-opacity-80">
              <div className="text-2xl font-bold">24/7</div>
              <div className="text-sm">Support</div>
            </div>
            <div className="text-white text-opacity-80">
              <div className="text-2xl font-bold">99.9%</div>
              <div className="text-sm">Uptime</div>
            </div>
            <div className="text-white text-opacity-80">
              <div className="text-2xl font-bold">256-bit</div>
              <div className="text-sm">Security</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden text-center">
            <div className="text-3xl font-bold gradient-text mb-2">
              xy<span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">leads</span>
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome back</h2>
            <p className="text-gray-600">Sign in to your employee account</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-red-800 text-sm">
                {error}
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <input
                  type="email"
                  {...register('email')}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  {...register('password')}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                />
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <a href="#" className="font-medium text-purple-600 hover:text-purple-500 transition-colors">
                  Forgot password?
                </a>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || employeeLoading}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 transition-all"
            >
              {isSubmitting || employeeLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="text-center">
            <Link 
              to="/" 
              className="text-gray-500 hover:text-purple-600 transition-colors text-sm"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
